import { modulo } from "@omni/utils/math.utils";

const charCheckFr = {
    0: 0,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    A: 10,
    B: 11,
    C: 12,
    D: 13,
    E: 14,
    F: 15,
    G: 16,
    H: 17,
    I: 18,
    J: 19,
    K: 20,
    L: 21,
    M: 22,
    N: 23,
    O: 24,
    P: 25,
    Q: 26,
    R: 27,
    S: 28,
    T: 29,
    U: 30,
    V: 31,
    W: 32,
    X: 33,
    Y: 34,
    Z: 35
};

const charCheckNl: Record<string, number> = {
    ...charCheckFr,
    "+": 36,
    "*": 37
};

// Mask + definition info: https://imask.js.org/guide.html#masked-pattern
const vatFormatting = {
    BE: {
        regex: new RegExp(/^[0-9]{10}$/),
        mask: "0000000000",
        placeHolderChar: "_"
    },
    FR: {
        regex: new RegExp(/^[a-zA-Z0-9]{2}[0-9]{9}$/),
        mask: "##000000000",
        definitions: { "#": /[0-9a-zA-Z]/ },
        placeHolderChar: "_"
    },
    NL: {
        regex: new RegExp(/^[0-9]{9}[bB][0-9]{2}$/),
        mask: "000000000{B}00",
        placeHolderChar: "_"
    },
    DE: {
        regex: new RegExp(/^[0-9]{9}$/),
        mask: "000000000",
        placeHolderChar: "_"
    },
    LU: {
        regex: new RegExp(/[0-9]{8}$/),
        mask: "00000000",
        placeHolderChar: "_"
    },
    OTHER: {
        regex: new RegExp(/^[0-9a-zA-Z]{8}([0-9a-zA-Z]{0,4})?$/)
    }
};

/**
 * Get the input mask of a given country
 * @param countryCode
 */
export const getCountryVatMask = (
    countryCode: "BE" | "DE" | "LU" | "FR" | "NL"
) => {
    return vatFormatting[countryCode] || vatFormatting.OTHER;
};

/**
 * Check the vat number for Belgium
 * @param vatNumber
 * @returns {boolean}
 */
export const doVatCheckBe = (vatNumber: string): boolean => {
    // First: check the regex
    if (!vatFormatting.BE.regex.test(vatNumber)) {
        return false;
    }

    // Do calculation check
    const vat = vatNumber.replace(/ /g, "");
    const lastCharacters = vat.slice(-2);
    const trimmedVat = parseInt(vat.substring(0, vat.length - 2), 10);
    return !(97 - (trimmedVat % 97) !== parseInt(lastCharacters, 10));
};

const oldCheckNl = (vatNumber: string) => {
    if (!vatFormatting.NL.regex.test(vatNumber)) {
        return false;
    }

    if (vatNumber.slice(-2) === "00") {
        return false;
    }

    let a1 = 0;
    for (let i = 0; i < 8; i++) {
        a1 += parseInt(vatNumber.charAt(i), 10) * (9 - i);
    }

    const a2 = a1 % 11;

    if (a2 === 10 || parseInt(vatNumber.charAt(8), 10) !== a2) {
        return false;
    }
    return true;
};

const newCheckNl = (vatNumber: string) => {
    const result = ["N", "L", ...vatNumber].reduce((acc, cur) => {
        const val = charCheckNl[cur];
        acc += val;
        return acc;
    }, "");

    return modulo(result, 97) === 1;
};

/**
 * Check the vat number for the Netherlands
 * @param vatNumber
 * @returns {boolean}
 */
export const doVatCheckNl = (vatNumber: string): boolean => {
    return oldCheckNl(vatNumber) || newCheckNl(vatNumber);
};

/**
 * Check the vat number for Luxembourg
 * @param vatNumber
 * @returns {boolean}
 */
export const doVatCheckLu = (vatNumber: string): boolean => {
    // First: check the regex
    if (!vatFormatting.LU.regex.test(vatNumber)) {
        return false;
    }

    // Do calculation check
    const lastCharacters = vatNumber.slice(-2);
    const trimmedVat = vatNumber.substring(0, vatNumber.length - 2);

    if (parseInt(trimmedVat, 10) % 89 !== parseInt(lastCharacters, 10)) {
        return false;
    }
    return true;
};

/**
 * Check the vat number for France
 * @param vatNumber
 * @returns {boolean}
 */
export const doVatCheckFr = (vatNumber: string): boolean => {
    // First: check the regex
    if (!vatFormatting.FR.regex.test(vatNumber)) {
        return false;
    }

    /* istanbul ignore else  */
    if (!Number.isNaN(parseInt(vatNumber.substring(0, 2), 10))) {
        // Old Method
        const r1 = parseInt(vatNumber.substring(0, 2), 10);
        const r2 =
            parseInt(`${vatNumber.substring(2, vatNumber.length)}12`, 10) % 97;

        if (r1 !== r2) {
            return false;
        }
    } else {
        // New Method ==> ignore testing can't find such VAT numbers
        const s1 = charCheckFr[vatNumber.charAt(0) as keyof typeof charCheckFr];
        const s2 = charCheckFr[vatNumber.charAt(1) as keyof typeof charCheckFr];
        let s = null;

        if (!Number.isNaN(parseInt(vatNumber.charAt(0), 10))) {
            if (!Number.isNaN(parseInt(vatNumber.charAt(1), 10))) {
                return false;
            }
            s = s1 * 24 + (s2 - 10);
        } else {
            s = s1 * 34 + (s2 - 100);
        }

        const p = s / 11 + 1;
        const r1 = s % 11;
        const r2 =
            (parseInt(vatNumber.substring(2, vatNumber.length), 10) + p) % 11;

        if (r1 !== r2) {
            return false;
        }
    }
    return true;
};

/**
 * Check the vat number for Germany
 * @param vatNumber
 * @returns {boolean}
 */
export const doVatCheckDe = (vatNumber: string): boolean => {
    // First: check the regex
    if (!vatFormatting.DE.regex.test(vatNumber)) {
        return false;
    }

    let p = 10;
    for (let i = 0; i < 8; i++) {
        const s = parseInt(vatNumber.charAt(i), 10) + p;
        const m = s % 10 === 0 ? 10 : s % 10;
        p = (2 * m) % 11;
    }

    const r = 11 - p === 10 ? 0 : 11 - p;
    if (parseInt(vatNumber.charAt(8), 10) !== r) {
        return false;
    }
    return true;
};

/**
 * Check the vat number for Germany
 * @param vatNumber
 * @returns {boolean}
 */
export const doVatCheckOther = (vatNumber: string): boolean => {
    return vatFormatting.OTHER.regex.test(vatNumber);
};
